import type {
  FormConfig,
  ListTotalConfig,
  Question,
  Section,
  Subsection,
} from '../../config/types';
import { useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { useFormConfig } from '../../hooks/useFormConfig';
import { useFormData } from '../../hooks/useFormData';
import { useLocation } from 'react-router-dom';
import { useAuthContext } from '../../auth/AuthContext';
import {
  convertExportHtmlToPdf,
  downloadExportExcel,
  downloadExportRenderedHtml,
} from '../../service/form';
import { Status } from '../../types';
import {
  extractConditionalQuestions,
  getEnabledSubsections,
  getSelectedConditionalQuestions,
  isJsonObject,
  isPageDisabled,
  isPageCompleted,
  isListCompleted,
  isSubsectionNotRequired,
  renderAnswer,
} from '../../utils/config-utils';
import {
  formatTotalValue,
  getListAnswers,
  getListTotals,
  interpolateQuestionText,
  interpolateQuestionTextWithAnswerSums,
} from '../../utils/form-utils';
import { getGdadProficiencyDerivedAnswers } from '../../utils/gdad-proficiency';
import { resolveTableauLicenseUsersAnswer } from '../../utils/tableau-license-users';
import { labelToTitle, questionTypeToKeys } from '../SummaryAnswers/SummaryAnswers';
import {
  getDynamicTableFieldName,
  resolveDynamicTableRowsFromAnswer,
} from '../form/DynamicTable';
import type { SubsectionRouterProps } from '../Router/SubsectionRouterProps';

const escapeHtml = (value: unknown) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const spin = keyframes`
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
`;

const DownloadLoadingOverlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 0.85);
`;

const DownloadLoadingSpinner = styled.div`
  width: 96px;
  height: 96px;
  border: 10px solid #b1b4b6;
  border-top-color: #1d70b8;
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
  margin: 0 auto;
`;

const DownloadLoadingLabel = styled.p`
  margin-top: 16px;
  margin-bottom: 0;
  text-align: center;
`;

const renderTagHtml = (status: Status) => {
  const tagClass =
    status === Status.Completed
      ? 'govuk-tag--green'
      : status === Status.InProgress
        ? 'govuk-tag--blue'
        : status === Status.NilReturn
          ? 'govuk-tag--yellow'
          : 'govuk-tag--grey';

  return `<strong class="govuk-tag ${tagClass}" style="display:inline-block;">${escapeHtml(status)}</strong>`;
};

const renderArrayAnswerHtml = (answer: string[]) => {
  if (!answer?.length) return '';
  return `<ul class="govuk-list govuk-!-margin-0">${answer
    .map((entry) => `<li>${escapeHtml(entry)}</li>`)
    .join('')}</ul>`;
};

const renderJsonObjectHtml = (answer: Record<string, string>, question: Question) => {
  const { columns, rows } = questionTypeToKeys(question.questionType);
  const rowHeader =
    question.questionType === 'coreCostInteralInput' ||
    question.questionType === 'coreCostExternalInput'
      ? 'Cost'
      : 'Grade';
  const isCoreCostTickbox =
    question.questionType === 'coreCostInteralInput' ||
    question.questionType === 'coreCostExternalInput';
  const isCoreCostSelected = (value: unknown) => {
    if (typeof value === 'number') return value === 1;
    if (typeof value === 'boolean') return value;
    if (typeof value !== 'string') return false;
    const normalized = value.trim().toLowerCase();
    return normalized === '1' || normalized === 'yes' || normalized === 'true';
  };
  const cellStyle =
    'padding:6px;border:1px solid #b1b4b6;background:transparent;color:inherit;vertical-align:middle;';

  const headerCells = columns
    .map(
      (column) =>
        `<th scope="col" class="govuk-table__header" style="${cellStyle}">${labelToTitle(column)}</th>`,
    )
    .join('');

  const bodyRows = rows
    .map((row) => {
      const cells =
        columns.length > 0
          ? columns
              .map(
                (column) => {
                  if (isCoreCostTickbox) {
                    const value = isCoreCostSelected(answer[`${row}${column}`]) ? '&#10003;' : '';
                    return `<td class="govuk-table__cell" style="${cellStyle} text-align:center;">${value}</td>`;
                  }
                  return `<td class="govuk-table__cell" style="${cellStyle}">${escapeHtml(answer[`${row}${column}`] ?? '-')}</td>`;
                },
              )
              .join('')
          : `<td class="govuk-table__cell" colSpan="${Math.max(columns.length, 1)}" style="${cellStyle}">${escapeHtml(
              answer[row] ?? '-',
            )}</td>`;

      return `<tr class="govuk-table__row">
          <th scope="row" class="govuk-table__header" style="${cellStyle} text-transform:capitalize;">
            ${labelToTitle(row)}
          </th>
          ${cells}
        </tr>`;
    })
    .join('');

  return `<table class="govuk-table" style="border-collapse:separate;border-spacing:0;margin:8px 0;width:auto;">
      <thead class="govuk-table__head">
        <tr class="govuk-table__row">
          <th scope="col" class="govuk-table__header" style="${cellStyle}">${rowHeader}</th>
          ${headerCells}
        </tr>
      </thead>
      <tbody class="govuk-table__body">${bodyRows}</tbody>
    </table>`;
};

const renderDynamicTableAnswerHtml = (
  answer: Record<string, string>,
  question: Question,
  allAnswers: Record<string, unknown>,
) => {
  if (!question.dynamicTable) return '';
  const config = resolveDynamicTableRowsFromAnswer(question.dynamicTable, allAnswers);

  const cellStyle =
    'padding:6px;border:1px solid #b1b4b6;background:transparent;color:inherit;vertical-align:middle;';
  const headerCells = config.columns
    .map(
      (column) =>
        `<th scope="col" class="govuk-table__header" style="${cellStyle}">${escapeHtml(column.label)}</th>`,
    )
    .join('');
  const bodyRows = config.rows
    .map((row) => {
      const cells = config.columns
        .map((column) => {
          const value = answer[getDynamicTableFieldName(row.key, column.key)] || '-';
          return `<td class="govuk-table__cell" style="${cellStyle}">${escapeHtml(value)}</td>`;
        })
        .join('');
      return `<tr class="govuk-table__row">
          <th scope="row" class="govuk-table__header" style="${cellStyle}">
            ${escapeHtml(row.label)}
          </th>
          ${cells}
        </tr>`;
    })
    .join('');

  return `<table class="govuk-table" style="border-collapse:separate;border-spacing:0;margin:8px 0;width:auto;">
      <thead class="govuk-table__head">
        <tr class="govuk-table__row">
          <th scope="col" class="govuk-table__header" style="${cellStyle}">${escapeHtml(config.rowHeader ?? 'Question')}</th>
          ${headerCells}
        </tr>
      </thead>
      <tbody class="govuk-table__body">${bodyRows}</tbody>
    </table>`;
};

const renderSingleAnswerHtml = (
  answer: unknown,
  question: Question,
  allAnswers: Record<string, unknown>,
) => {
  const resolvedAnswer = resolveTableauLicenseUsersAnswer(question.name, answer, allAnswers);
  if (Array.isArray(resolvedAnswer)) return renderArrayAnswerHtml(resolvedAnswer);
  if (question.questionType === 'dynamicTable' && isJsonObject(resolvedAnswer)) {
    return renderDynamicTableAnswerHtml(
      resolvedAnswer as Record<string, string>,
      question,
      allAnswers,
    );
  }
  if (isJsonObject(resolvedAnswer)) {
    return renderJsonObjectHtml(resolvedAnswer as Record<string, string>, question);
  }
  const safeValue = String(resolvedAnswer ?? '').trim();
  return safeValue ? escapeHtml(safeValue) : '';
};

const renderSummaryRowHtml = (
  question: Question,
  formattedAnswers:
    | string
    | string[]
    | Record<string, string>
    | (string | string[] | Record<string, string>)[],
  allAnswers?: Record<string, unknown>,
) => {
  const answersArray = Array.isArray(formattedAnswers) ? formattedAnswers : [formattedAnswers];
  const multiple = answersArray.length > 1;
  const resolvedQuestionTitle =
    interpolateQuestionTextWithAnswerSums(question.title, question, allAnswers) || question.title;

  const content = multiple
    ? `<ul class="govuk-list govuk-!-margin-0">${answersArray
        .map((ans, index) => {
          const divider =
            index < answersArray.length - 1
              ? 'margin-bottom:10px;padding-bottom:10px;border-bottom:1px solid #b1b4b6;'
              : '';
          return `<li style="${divider}">${renderSingleAnswerHtml(ans, question, allAnswers || {}) || '-'}</li>`;
        })
        .join('')}</ul>`
    : renderSingleAnswerHtml(answersArray[0], question, allAnswers || {}) || '-';

  return `<tr class="govuk-table__row">
      <td class="govuk-table__cell" style="padding:6px;">${escapeHtml(resolvedQuestionTitle)}</td>
      <td class="govuk-table__cell" colSpan="2" style="padding:6px;">${content}</td>
    </tr>`;
};

const renderListAnswersHtml = (
  formValues: Record<string, unknown>,
  questions: Question[],
  listLabel?: string,
) => {
  const grouped = getListAnswers(formValues, questions);
  const sortedIds = Object.keys(grouped).sort((a, b) => Number(a) - Number(b));

  return sortedIds
    .map((id, index) => {
      const label = listLabel || 'Response';
      const rows = grouped[id]
        .map(({ question, answer }) =>
          renderSummaryRowHtml(question, renderAnswer(question, answer), formValues),
        )
        .join('');

      return `<tr class="govuk-table__row">
            <td class="govuk-table__cell" colSpan="3" style="padding:6px;background:#f3f2f1;font-weight:bold;border-top:2px solid #b1b4b6;">
              ${escapeHtml(label)} ${index + 1}
            </td>
          </tr>${rows}`;
    })
    .join('');
};

const renderListTotalsHtml = (
  formValues: Record<string, unknown>,
  questions: Question[],
  total: ListTotalConfig,
) => {
  if (!total.columns?.length) return '';

  const { groupBy, rows, hasEntries } = getListTotals(formValues, questions, total);
  if (!hasEntries) return '';

  const getQuestionTitle = (name: string) => {
    const question = questions.find((candidate) => candidate.name === name);
    const questionTitle = question?.title;
    return question
      ? interpolateQuestionTextWithAnswerSums(questionTitle, question, formValues) ||
          questionTitle ||
          name
      : interpolateQuestionText(questionTitle, formValues) || questionTitle || name;
  };

  const headers = `${groupBy
    .map(
      (fieldName) =>
        `<th class="govuk-table__header">${escapeHtml(getQuestionTitle(fieldName))}</th>`,
    )
    .join('')}${total.columns
    .map((column) => `<th class="govuk-table__header">${escapeHtml(column.title)}</th>`)
    .join('')}`;

  const bodyRows = rows
    .map(
      (row) =>
        `<tr class="govuk-table__row">${groupBy
          .map(
            (_fieldName, index) =>
              `<td class="govuk-table__cell">${escapeHtml(row.groupValues[index])}</td>`,
          )
          .join('')}${row.totals
          .map(
            (value, index) =>
              `<td class="govuk-table__cell">${escapeHtml(
                formatTotalValue(
                  value,
                  questions.find((question) => question.name === total.columns[index].field),
                ),
              )}</td>`,
          )
          .join('')}</tr>`,
    )
    .join('');

  return `<tr class="govuk-table__row">
      <td class="govuk-table__cell" colSpan="3" style="padding:6px;font-weight:bold;background:#f3f2f1;">Totals</td>
    </tr>
    <tr class="govuk-table__row">
      <td class="govuk-table__cell" colSpan="3" style="padding:6px;">
        <table class="govuk-table govuk-!-margin-0">
          <thead class="govuk-table__head"><tr class="govuk-table__row">${headers}</tr></thead>
          <tbody class="govuk-table__body">${bodyRows}</tbody>
        </table>
      </td>
    </tr>`;
};

const getStatusForPage = (
  questions: Question[],
  answers: Record<string, unknown>,
  draftsForSubsection: string[],
  page: { name: string; type?: string; pageType?: string },
) => {
  if (draftsForSubsection?.includes(page.name)) return Status.InProgress;
  const pageHasResponses = questions.some(({ name }) =>
    Object.keys(answers).some((key) => key === name || key.startsWith(`${name}-`)),
  );
  if (page.type === 'nilreturn') {
    if (!pageHasResponses) return Status.NilReturn;
    return isPageCompleted(questions, answers) ? Status.Completed : Status.InProgress;
  }
  if (page.pageType === 'list') {
    return isListCompleted(questions, answers) ? Status.Completed : Status.NotStarted;
  }
  if (isPageCompleted(questions, answers)) return Status.Completed;
  return Status.NotStarted;
};

const renderSelectedConditionalRowsHtml = (
  question: Question,
  answers: Record<string, unknown>,
): string => {
  const rawAnswer = answers[question.name];
  const selectedAnswer = Array.isArray(rawAnswer) ? rawAnswer.map(String) : String(rawAnswer ?? '');

  return getSelectedConditionalQuestions(question, selectedAnswer)
    .map((conditional) => {
      if (conditional.questionType === 'content') {
        return renderSelectedConditionalRowsHtml(conditional, answers);
      }

      return (
        renderSummaryRowHtml(
          conditional,
          renderAnswer(conditional, answers[conditional.name]),
          answers,
        ) + renderSelectedConditionalRowsHtml(conditional, answers)
      );
    })
    .join('');
};

const isRewardNomineePageHidden = (
  tenant: string | undefined,
  page: { name: string },
  answers: Record<string, unknown>,
): boolean => {
  if (tenant !== 'reward') return false;

  const nominationKind = String(answers['nomination-kind'] ?? '').trim().toLowerCase();
  if (nominationKind === 'team') return page.name === 'individual-nominee';
  if (nominationKind === 'individual') return page.name === 'team-nominee';
  return false;
};

const renderPageTableRows = (
  tenant: string | undefined,
  subsectionSlug: string,
  page: any,
  answers: Record<string, unknown>,
  drafts: Record<string, string[]>,
  pageEnabled: boolean,
  subsectionNotRequired: boolean,
) => {
  if (isRewardNomineePageHidden(tenant, page, answers)) {
    return '';
  }

  if (tenant === 'reward' && page.name === 'team-nominee') {
    const nomineeIndexes = new Set(
      Object.entries(answers).flatMap(([key, value]) => {
        const match = key.match(/^(\d+)-nominee-(?:full-name|email|staff-number)$/);
        return match && String(value ?? '').trim() ? [match[1]] : [];
      }),
    );
    answers = Object.fromEntries(
      Object.entries(answers).filter(([key]) => {
        const match = key.match(/^(\d+)-nominee-/);
        return !match || nomineeIndexes.has(match[1]);
      }),
    );
  }

  const { title, pageType, questions = [] } = page;
  const draftsForSubsection = drafts?.[subsectionSlug] || [];
  const extractedQuestions = extractConditionalQuestions(questions);
  const pageHasResponses = extractedQuestions.some(({ name }) =>
    Object.keys(answers).some((key) => key === name || key.startsWith(`${name}-`)),
  );
  if (pageType === 'question' && page.type === 'nilreturn' && !pageHasResponses) {
    return '';
  }
  const status =
    !pageEnabled && page.type === 'nilreturn'
      ? Status.NilReturn
      : getStatusForPage(
          pageType === 'list' ? questions : extractedQuestions,
          answers,
          draftsForSubsection,
          page,
        );

  let customTagText = '';
  let customTagClass = 'govuk-tag--blue';
  if (pageType === 'list' && page.type === 'nilreturn') {
    if (!pageEnabled) {
      customTagText = 'Nil Return';
      customTagClass = 'govuk-tag--yellow';
    } else {
      const baseQuestions = Array.isArray(page.questions) ? page.questions : [];
      const questionsForResponses = extractConditionalQuestions(baseQuestions);
      const grouped = getListAnswers(answers, questionsForResponses);
      const responseCount = Object.keys(grouped).length;

      if (responseCount === 0 && status === Status.NilReturn) {
        customTagText = 'Nil Return';
        customTagClass = 'govuk-tag--yellow';
      } else if (status === Status.InProgress) {
        customTagText = 'In progress';
        customTagClass = 'govuk-tag--blue';
      } else if (
        responseCount > 0 &&
        (status === Status.Completed || status === Status.NilReturn)
      ) {
        const label = page.listLabel || 'Response';
        const labelText = responseCount === 1 ? label : `${label}s`;
        customTagText = `${responseCount} ${labelText}`;
        customTagClass = 'govuk-tag--green';
      }
    }
  }

  const infoStatusCell =
    pageType === 'information'
      ? `<span class="govuk-tag govuk-tag--grey">INFORMATION PAGE</span>`
      : null;

  const statusCell = subsectionNotRequired
    ? '<strong class="govuk-tag govuk-tag--green" style="display:inline-block;">Not Required</strong>'
    : pageType === 'information'
      ? infoStatusCell
      : pageType === 'question'
        ? renderTagHtml(status)
        : customTagText
          ? `<span class="govuk-tag ${customTagClass}">${escapeHtml(customTagText)}</span>`
          : renderTagHtml(status);

  const pageHeaderRow = `<tr class="govuk-table__row">
      <th scope="row" class="govuk-table__header govuk-!-width-one-half" style="padding-top:30px;padding-bottom:15px;">
        ${escapeHtml(title)}
      </th>
      <td class="govuk-table__cell" colSpan="2" style="padding-top:30px;padding-bottom:15px;">${statusCell}</td>
    </tr>`;

  let detailRows = '';

  if (pageType === 'question') {
    detailRows = questions
      .map((question: Question) => {
        const allMatching = Object.keys(answers).filter(
          (key) => key === question.name || key.startsWith(`${question.name}-`),
        );

        const isCoreCostTickboxQuestion =
          question.questionType === 'coreCostInteralInput' ||
          question.questionType === 'coreCostExternalInput';
        const formattedAnswers = allMatching.map((key) => {
          const rawAnswer = answers[key];
          // Preserve object shape for Core Cost tickbox inputs so HTML export
          // renders ticks/blanks instead of flattening to 1/0 lists.
          if (isCoreCostTickboxQuestion && isJsonObject(rawAnswer)) {
            return rawAnswer as Record<string, string>;
          }
          return renderAnswer(question, rawAnswer);
        });

        const conditionalRows = renderSelectedConditionalRowsHtml(question, answers);

        return `${renderSummaryRowHtml(question, formattedAnswers, answers)}${conditionalRows}`;
      })
      .join('');
  }
  if (tenant === 'gdad' && pageType === 'question' && page.name === 'proficiency-rating') {
    const proficiencyRows = getGdadProficiencyDerivedAnswers(answers)
      .map((derivedAnswer) =>
        renderSummaryRowHtml(
          {
            name: derivedAnswer.name,
            title: derivedAnswer.title,
            questionType: 'textInput',
          },
          derivedAnswer.value,
          answers,
        ),
      )
      .join('');
    detailRows = `${proficiencyRows}${detailRows}`;
  }

  if (pageType === 'list') {
    const questionsForResponses = extractConditionalQuestions(questions);
    const totalsRows = page.total ? renderListTotalsHtml(answers, questionsForResponses, page.total) : '';
    const answerRows = renderListAnswersHtml(answers, questionsForResponses, page.listLabel);
    detailRows = `${totalsRows}${answerRows}`;
  }

  return `${pageHeaderRow}${detailRows}`;
};

const collectPageQuestionNames = (page: any, names: string[] = [], parent?: string) => {
  const walk = (qs: any[], parentKey?: string) => {
    if (!Array.isArray(qs)) return;
    qs.forEach((q) => {
      const name = q?.name;
      if (name) {
        names.push(parentKey ? `${parentKey}.${name}` : name);
      }
      if (q?.options) {
        q.options.forEach((opt: any) => {
          if (opt?.questions) {
            walk(opt.questions, name || parentKey);
          }
        });
      }
    });
  };

  walk(page?.questions || [], parent);
  return names;
};

const buildOrderedAnswers = (cfg: FormConfig | undefined, answers: Record<string, unknown>) => {
  const ordered: Record<string, unknown> = {};
  const skipped: Set<string> = new Set();

  if (!cfg?.sections) return ordered;

  cfg.sections.forEach((section) => {
    section.subsections.forEach((subsection) => {
      subsection.pages.forEach((page) => {
        const baseNames = collectPageQuestionNames(page, []);
        if (!baseNames.length) return;

        const prefixes = new Set<number>();
        const hasPrefixedFor = new Set<string>();
        Object.keys(answers || {}).forEach((key) => {
          const match = key.match(/^(\d+)-(.*)$/);
          if (!match) return;
          const [, numStr, rest] = match;
          if (baseNames.includes(rest)) {
            hasPrefixedFor.add(rest);
            const n = Number(numStr);
            if (!Number.isNaN(n)) prefixes.add(n);
          }
        });

        if (page.pageType === 'list') {
          const sortedPrefixes = Array.from(prefixes).sort((a, b) => a - b);
          if (!sortedPrefixes.length) {
            baseNames.forEach((name) => {
              const key = `1-${name}`;
              if (!(key in ordered)) {
                const val = answers[key] ?? (answers as any)[name] ?? '';
                ordered[key] = val ?? '';
              }
            });
          } else {
            let newIdx = 1;
            sortedPrefixes.forEach((oldIdx) => {
              baseNames.forEach((name) => {
                const oldKey = `${oldIdx}-${name}`;
                const newKey = `${newIdx}-${name}`;
                if (!(newKey in ordered)) {
                  ordered[newKey] = (answers as any)[oldKey] ?? '';
                }
                skipped.add(oldKey);
              });
              newIdx += 1;
            });
          }
        } else {
          baseNames.forEach((name) => {
            if (hasPrefixedFor.has(name) && !(name in answers)) return;
            if (!(name in ordered)) {
              ordered[name] = (answers as any)[name] ?? '';
            }
          });
        }
      });
    });
  });

  // include any remaining keys not skipped, preserving existing order
  Object.entries(answers || {}).forEach(([k, v]) => {
    if (skipped.has(k)) return;
    if (!(k in ordered)) ordered[k] = v;
  });

  return ordered;
};

export const buildExportHtml = (
  tenant: string | undefined,
  formTitle: string | undefined,
  submittedAt: string | undefined,
  sections: Section[],
  answers: Record<string, unknown>,
  drafts: Record<string, string[]>,
  enabledSubsections: Record<string, Subsection>,
  formId?: string,
  rewardPanelDecision?: string,
  rewardPanelNotes?: string,
) => {
  let submittedDisplay = '';
  if (submittedAt) {
    const parsed = new Date(submittedAt);
    submittedDisplay = Number.isNaN(parsed.getTime())
      ? submittedAt
      : parsed.toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
  }

  const headingTitle = escapeHtml(formTitle || 'Check your answers');
  const subHeading = submittedDisplay
    ? `Copy of Submitted Form (${escapeHtml(submittedDisplay)})`
    : 'Copy of Submitted Form';

  const configuredSectionsHtml = (sections || [])
    .map((section) => {
      const subsectionHtml = section.subsections
        .map((subsection) => {
          const enabledPageNames =
            enabledSubsections?.[subsection.slug]?.pages?.map((p) => p.name) || null;

          const pagesForExport = subsection.dynamicPages
            ? enabledSubsections?.[subsection.slug]?.pages || []
            : subsection.pages;
          const subsectionNotRequired = isSubsectionNotRequired(subsection, { answers });

          const rows = pagesForExport
            .map((page) => {
              const pageEnabledByConfig = !enabledPageNames || enabledPageNames.includes(page.name);
              const pageEnabled =
                pageEnabledByConfig && !isPageDisabled(page, { answers });
              return renderPageTableRows(
                tenant,
                subsection.slug,
                page,
                answers,
                drafts,
                pageEnabled,
                subsectionNotRequired,
              );
            })
            .join('');

          return `<li class="summary-list__item">
              <div class="summary-list__item-header">
                <h3 class="govuk-heading-m" style="margin-bottom:0;">${escapeHtml(subsection.title)}</h3>
              </div>
              <div class="govuk-body">
                <table class="govuk-table govuk-!-margin-top-2">
                  <tbody class="govuk-table__body">${rows}</tbody>
                </table>
              </div>
            </li>`;
        })
        .join('');

      return `<section class="govuk-!-margin-bottom-4">
          <h2 class="govuk-heading-l" style="margin-bottom:12px;">${escapeHtml(section.title)}</h2>
          <ol class="govuk-list">${subsectionHtml}</ol>
        </section>`;
    })
    .join('');
  const rewardPanelDecisionHtml =
    tenant === 'reward' && rewardPanelDecision
      ? `<section class="govuk-!-margin-bottom-4">
          <h2 class="govuk-heading-l" style="margin-bottom:12px;">Panel decision</h2>
          <table class="govuk-table govuk-!-margin-top-2">
            <tbody class="govuk-table__body">
              <tr class="govuk-table__row">
                <th scope="row" class="govuk-table__header govuk-!-width-one-half">Does the panel approve this nomination?</th>
                <td class="govuk-table__cell">${escapeHtml(rewardPanelDecision)}</td>
              </tr>
              <tr class="govuk-table__row">
                <th scope="row" class="govuk-table__header govuk-!-width-one-half">Notes (optional)</th>
                <td class="govuk-table__cell">${escapeHtml(rewardPanelNotes || '')}</td>
              </tr>
            </tbody>
          </table>
        </section>`
      : '';
  const sectionsHtml = `${configuredSectionsHtml}${rewardPanelDecisionHtml}`;

  return `<!doctype html>
  <html lang="en">
    <head>
      <meta charset="UTF-8"/>
      <title>${headingTitle}</title>
      <style>
        @page { size: A4; margin: 5mm; }
        body { font-family: Arial, sans-serif; padding: 0; background: #fff; color: #0b0c0c; }
        .export-container { max-width: 1200px; margin: 0 auto; }
        .govuk-heading-xl { font-size: 2rem; margin: 0 0 1rem; }
        .govuk-heading-l { font-size: 1.35rem; margin: 1.5rem 0 0.5rem; }
        .govuk-body { font-size: 0.95rem; line-height: 1.5; }
        .govuk-table { border-collapse: collapse; width: auto; margin-top: 4px; table-layout: auto; }
        .govuk-table th, .govuk-table td { border: 1px solid #b1b4b6; padding: 6px; vertical-align: top; font-size: 0.95rem; }
        .govuk-table__header { text-align: left; background: #f3f2f1; }
        .govuk-table__row th.govuk-!-width-one-half { border-top: none; border-left: none; background: transparent; }
        .govuk-table__row td { border-top: none; }
        .govuk-table__body tr:first-child th, .govuk-table__body tr:first-child td { border-top: 2px solid #b1b4b6; }
        .govuk-tag { display: inline-block; padding: 4px 8px; border-radius: 4px; font-weight: bold; text-transform: uppercase; letter-spacing: 0.5px; }
        .govuk-tag--green { background:#00703c; color:#fff; }
        .govuk-tag--blue { background:#1d70b8; color:#fff; }
        .govuk-tag--yellow { background:#ffdd00; color:#0b0c0c; }
        .govuk-tag--grey { background:#b1b4b6; color:#0b0c0c; }
        .summary-list__item { list-style: none; border-bottom: 1px solid #b1b4b6; padding-bottom: 12px; }
        .summary-list__item-header { display: flex; justify-content: space-between; align-items: center; }
        .govuk-list { padding-left: 0; margin: 0; list-style: none; }
        .govuk-list li { margin: 0 0 8px; }
        table ul { margin: 0; padding-left: 18px; list-style: disc; }
      </style>
    </head>
    <body>
      <main class="export-container">
        <h1 class="govuk-heading-xl">${headingTitle}</h1>
        <p class="govuk-body">${subHeading}</p>
        <hr style="border:1px solid #b1b4b6; margin: 10px 0 24px 0;"/>
        ${sectionsHtml}
        <p class="govuk-body" style="margin-top:32px;font-size:0.85rem;color:#505a5f;">
          [${escapeHtml(formId || '')}] Government Research &amp; Insights Platform (GRID)
        </p>
      </main>
    </body>
  </html>`;
};

const Download = () => {
  const { formConfig } = useFormConfig();
  const { data } = useFormData();
  const { fetchJWT } = useAuthContext();
  const location = useLocation();
  const [downloadError, setDownloadError] = useState<string>('');
  const [isExcelDownloading, setIsExcelDownloading] = useState<boolean>(false);
  const [isHtmlDownloading, setIsHtmlDownloading] = useState<boolean>(false);
  const [isPdfDownloading, setIsPdfDownloading] = useState<boolean>(false);
  const [selectedFormat, setSelectedFormat] = useState<string>('');
  const locationFormId = (location.state as any)?.formId as string | undefined;

  const derivedFormId = locationFormId || (data as any)?.formId || formConfig?.id;
  const answers =
    data?.answers && typeof data.answers === 'object'
      ? (data.answers as Record<string, unknown>)
      : {};
  const drafts =
    data?.drafts && typeof data.drafts === 'object'
      ? (data.drafts as Record<string, string[]>)
      : {};
  const enabledSubsectionsForExport = formConfig
    ? getEnabledSubsections(formConfig, data as any)
    : {};

  const buildRenderedExportDocument = () =>
    buildExportHtml(
      formConfig?.id,
      formConfig?.title,
      (data as any)?.submittedAt,
      formConfig?.sections || [],
      answers,
      drafts,
      enabledSubsectionsForExport,
      derivedFormId,
      (data as any)?.rewardPanelDecision,
      (data as any)?.rewardPanelNotes,
    );

  const handleDownload = async () => {
    setDownloadError('');
    if (!derivedFormId) {
      setDownloadError('Unable to download Excel for this form.');
      return;
    }
    const filenameBase =
      formConfig?.title
        ?.replace(/[^a-z0-9]+/gi, '-')
        .replace(/(^-|-$)/g, '')
        .toLowerCase() || 'answers';

    try {
      setIsExcelDownloading(true);
      const token = (await fetchJWT()).idToken;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const { blob, filename } = await downloadExportExcel(token, { form_id: derivedFormId });
      const finalName = filename || `${filenameBase}.xlsx`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Excel download failed', error);
      setDownloadError('Unable to download Excel right now.');
    } finally {
      setIsExcelDownloading(false);
    }
  };

  const handleDownloadHtml = async () => {
    setDownloadError('');
    if (!derivedFormId) {
      setDownloadError('Unable to download HTML for this form.');
      return;
    }
    const filenameBase =
      formConfig?.title
        ?.replace(/[^a-z0-9]+/gi, '-')
        .replace(/(^-|-$)/g, '')
        .toLowerCase() || 'answers';

    try {
      setIsHtmlDownloading(true);
      const token = (await fetchJWT()).idToken;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const { blob, filename } = await downloadExportRenderedHtml(token, {
        html: buildRenderedExportDocument(),
        filename: filenameBase,
        formVersion: (data as any)?.formVersion || (data as any)?.form_version,
      });
      const finalName = filename || `${filenameBase}.html`;

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = finalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('HTML download failed', error);
      setDownloadError('Unable to download HTML right now.');
    } finally {
      setIsHtmlDownloading(false);
    }
  };

  const handleDownloadPdf = async () => {
    setDownloadError('');
    if (!derivedFormId) {
      setDownloadError('Unable to download PDF for this form.');
      return;
    }
    const filenameBase =
      formConfig?.title
        ?.replace(/[^a-z0-9]+/gi, '-')
        .replace(/(^-|-$)/g, '')
        .toLowerCase() || 'answers';

    try {
      setIsPdfDownloading(true);
      const token = (await fetchJWT()).idToken;
      if (!token) {
        throw new Error('Not authenticated');
      }

      const { blob, filename } = await convertExportHtmlToPdf(token, {
        html: buildRenderedExportDocument(),
        filename: filenameBase,
        formVersion: (data as any)?.formVersion || (data as any)?.form_version,
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `${filenameBase}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF download failed', error);
      setDownloadError('Unable to download PDF right now.');
    } finally {
      setIsPdfDownloading(false);
    }
  };

  const isDownloading = isExcelDownloading || isHtmlDownloading || isPdfDownloading;

  const handleSelectedDownload = async () => {
    if (selectedFormat === 'html') {
      await handleDownloadHtml();
      return;
    }
    if (selectedFormat === 'pdf') {
      await handleDownloadPdf();
      return;
    }
    if (selectedFormat === 'excel') {
      await handleDownload();
    }
  };

  return (
    <>
      {isDownloading && (
        <DownloadLoadingOverlay
          role="status"
          aria-live="polite"
          aria-label="Preparing download"
        >
          <div>
            <DownloadLoadingSpinner />
            <DownloadLoadingLabel className="govuk-body-l">
              Preparing download
            </DownloadLoadingLabel>
          </div>
        </DownloadLoadingOverlay>
      )}
      <h3 className="govuk-heading-m">Download</h3>
      <p className="govuk-body">Click below to download your answers</p>
      {downloadError && (
        <p className="govuk-error-message" role="alert">
          {downloadError}
        </p>
      )}

      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div className="govuk-form-group govuk-!-margin-bottom-0">
          <label className="govuk-label govuk-visually-hidden" htmlFor="download-format-select">
            Download format
          </label>
          <select
            id="download-format-select"
            className="govuk-select"
            value={selectedFormat}
            onChange={(event) => setSelectedFormat(String(event.target.value || '').trim().toLowerCase())}
            disabled={isDownloading}
          >
            <option value="">Select format</option>
            <option value="html">HTML</option>
            <option value="pdf">PDF</option>
            <option value="excel">Excel</option>
          </select>
        </div>
        <button
          className="govuk-button"
          type="button"
          data-module="govuk-button"
          data-prevent-double-click="true"
          onClick={() => void handleSelectedDownload()}
          disabled={isDownloading || selectedFormat.length === 0}
        >
          {isDownloading ? 'Downloading...' : 'Download'}
        </button>
      </div>
    </>
  );
};

export default Download;
